import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { createLink, LinkProps } from '@tanstack/react-router';

interface TableOverlayProps {
  isLoading: boolean;
  isFetching: boolean;
  hasData: boolean;
  showCircularProgressOnly: boolean;
  isError: boolean;
  emptyIcon?: React.ReactNode;
  emptyHeading: string;
  emptyText: string;
  errorText: string;
  ctaText?: string;
  ctaLink?: LinkProps['to'];
  emptyActions?: React.ReactNode;
  isPaginated: boolean;
  headerHeight: number;
  paginationHeight: number;
}

const RouterLink = createLink(Link);

interface EmptyOverlayContentProps {
  isError: boolean;
  emptyIcon?: React.ReactNode;
  emptyHeading: string;
  emptyText: string;
  errorText: string;
  ctaText?: string;
  ctaLink?: LinkProps['to'];
  emptyActions?: React.ReactNode;
}

const EmptyOverlayContent = ({
  isError,
  emptyIcon,
  emptyHeading,
  emptyText,
  errorText,
  ctaText,
  ctaLink,
  emptyActions,
}: EmptyOverlayContentProps) => (
  <>
    {!isError && emptyIcon ? <Box mb={1}>{emptyIcon}</Box> : null}
    <Typography variant='h6' color='text.secondary' gutterBottom>
      {isError ? 'An error occurred' : emptyHeading}
    </Typography>
    <Typography variant='body2' color='text.secondary' align='center'>
      {isError ? errorText : emptyText}
    </Typography>

    {!isError && ctaText && ctaLink ? (
      <RouterLink
        to={ctaLink}
        mt={1.5}
        underline='hover'
        sx={{ color: 'primary.600' }}
      >
        {ctaText}
      </RouterLink>
    ) : null}

    {!isError && emptyActions ? <Box mt={2}>{emptyActions}</Box> : null}
  </>
);

const TableOverlay = ({
  isLoading,
  isFetching,
  hasData,
  showCircularProgressOnly,
  isError,
  emptyIcon,
  emptyHeading,
  emptyText,
  errorText,
  ctaText,
  ctaLink,
  emptyActions,
  isPaginated,
  headerHeight,
  paginationHeight,
}: TableOverlayProps) => {
  const showSpinner =
    isLoading ||
    (isFetching && !hasData) ||
    (showCircularProgressOnly && isFetching);

  return (
    <Box
      position='absolute'
      left={0}
      right={0}
      top={headerHeight}
      bottom={paginationHeight ? paginationHeight + 1 : 0}
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        borderRadius: isPaginated ? 0 : 1,
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {showSpinner ? (
          <CircularProgress />
        ) : (
          <EmptyOverlayContent
            isError={isError}
            emptyIcon={emptyIcon}
            emptyHeading={emptyHeading}
            emptyText={emptyText}
            errorText={errorText}
            ctaText={ctaText}
            ctaLink={ctaLink}
            emptyActions={emptyActions}
          />
        )}
      </Box>
    </Box>
  );
};

export default TableOverlay;
